import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Table, Alert } from "react-bootstrap";
import {
  BOOKING_STATUS_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  PASSENGER_STATUS_OPTIONS,
  BERTH_TYPE_OPTIONS,
  GENDER_OPTIONS,
  PASSENGER_TYPE_OPTIONS,
  emptyPassenger,
  emptyTicket,
  calcFare,
  preparePayload,
  mapTicketToForm,
} from "./trainTicketConstants";
import {
  SaveTrainTicket,
  PreviewTrainTicket,
  DownloadTrainTicketPdf,
} from "../../entities/action/action";
import TrainTicketPreview from "./trainTicketPreview";
import classes from "./ticketGenerator.module.css";

const Section = ({ title, children }) => (
  <div className={classes.Section}>
    <h5 className={classes.SectionTitle}>{title}</h5>
    {children}
  </div>
);

const TrainTicketForm = ({ initialData, onSaved }) => {
  const [ticket, setTicket] = useState(initialData || emptyTicket());
  const [previewHtml, setPreviewHtml] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialData) setTicket(mapTicketToForm(initialData));
  }, [initialData]);

  const update = (field, value) => setTicket((t) => ({ ...t, [field]: value }));

  const updatePassenger = (index, field, value) => {
    setTicket((t) => {
      const passengers = [...t.passengers];
      passengers[index] = { ...passengers[index], [field]: value };
      return { ...t, passengers };
    });
  };

  const addPassenger = () =>
    setTicket((t) => ({ ...t, passengers: [...t.passengers, emptyPassenger()] }));

  const removePassenger = (index) => {
    if (ticket.passengers.length <= 1) return;
    setTicket((t) => ({
      ...t,
      passengers: t.passengers.filter((_, i) => i !== index),
    }));
  };

  const movePassenger = (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= ticket.passengers.length) return;
    setTicket((t) => {
      const passengers = [...t.passengers];
      [passengers[index], passengers[next]] = [passengers[next], passengers[index]];
      return { ...t, passengers };
    });
  };

  const fareTotal = calcFare(ticket);

  const handlePreview = async () => {
    setError("");
    const res = await PreviewTrainTicket(preparePayload(ticket));
    if (res?.success && res?.previewHtml) {
      setPreviewHtml(res.previewHtml);
      setShowPreview(true);
    } else {
      setError(res?.message || "Preview failed");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    const res = await SaveTrainTicket(preparePayload(ticket));
    setSaving(false);
    if (res?.success) {
      setTicket(res.ticket);
      setShowPreview(false);
      if (onSaved) onSaved(res.ticket);
    } else {
      setError(res?.message || "Save failed");
    }
  };

  const handleDownload = async () => {
    await DownloadTrainTicketPdf(preparePayload(ticket), ticket.pnr, ticket.bookingId);
  };

  const fareField = (label, field) => (
    <Form.Group as={Col} md={4} sm={6}>
      <Form.Label>{label}</Form.Label>
      <Form.Control
        type="number"
        min="0"
        step="0.01"
        value={ticket[field]}
        onChange={(e) => update(field, e.target.value)}
      />
    </Form.Group>
  );

  return (
    <div className={classes.Box}>
      {error && <Alert variant="danger">{error}</Alert>}

      <Section title="Booking Details">
        <Row>
          <Form.Group as={Col} md={4}><Form.Label>Booking ID</Form.Label><Form.Control value={ticket.bookingId} onChange={(e) => update("bookingId", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>PNR</Form.Label><Form.Control value={ticket.pnr} onChange={(e) => update("pnr", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Ticket Number</Form.Label><Form.Control value={ticket.ticketNumber} onChange={(e) => update("ticketNumber", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Booking Date</Form.Label><Form.Control type="date" value={ticket.bookingDate} onChange={(e) => update("bookingDate", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Booking Status</Form.Label><Form.Control as="select" value={ticket.bookingStatus} onChange={(e) => update("bookingStatus", e.target.value)}>{BOOKING_STATUS_OPTIONS.map((o) => <option key={o}>{o}</option>)}</Form.Control></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Payment Status</Form.Label><Form.Control as="select" value={ticket.paymentStatus} onChange={(e) => update("paymentStatus", e.target.value)}>{PAYMENT_STATUS_OPTIONS.map((o) => <option key={o}>{o}</option>)}</Form.Control></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Issued By</Form.Label><Form.Control value={ticket.issuedBy} onChange={(e) => update("issuedBy", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Agent/Organization</Form.Label><Form.Control value={ticket.agentOrganizationName} onChange={(e) => update("agentOrganizationName", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Special Reference</Form.Label><Form.Control value={ticket.specialReferenceNumber} onChange={(e) => update("specialReferenceNumber", e.target.value)} /></Form.Group>
        </Row>
      </Section>

      <Section title="Train Details">
        <Row>
          <Form.Group as={Col} md={3}><Form.Label>Train Number</Form.Label><Form.Control value={ticket.trainNumber} onChange={(e) => update("trainNumber", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={5}><Form.Label>Train Name</Form.Label><Form.Control value={ticket.trainName} onChange={(e) => update("trainName", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Train Type</Form.Label><Form.Control value={ticket.trainType} onChange={(e) => update("trainType", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Journey Date</Form.Label><Form.Control type="date" value={ticket.journeyDate} onChange={(e) => update("journeyDate", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Boarding Station</Form.Label><Form.Control value={ticket.boardingStation} onChange={(e) => update("boardingStation", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Boarding Code</Form.Label><Form.Control value={ticket.boardingStationCode} onChange={(e) => update("boardingStationCode", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Destination Station</Form.Label><Form.Control value={ticket.destinationStation} onChange={(e) => update("destinationStation", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Destination Code</Form.Label><Form.Control value={ticket.destinationStationCode} onChange={(e) => update("destinationStationCode", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Departure Date</Form.Label><Form.Control type="date" value={ticket.departureDate} onChange={(e) => update("departureDate", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Departure Time</Form.Label><Form.Control type="time" value={ticket.departureTime} onChange={(e) => update("departureTime", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Arrival Date</Form.Label><Form.Control type="date" value={ticket.arrivalDate} onChange={(e) => update("arrivalDate", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Arrival Time</Form.Label><Form.Control type="time" value={ticket.arrivalTime} onChange={(e) => update("arrivalTime", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Duration</Form.Label><Form.Control value={ticket.journeyDuration} onChange={(e) => update("journeyDuration", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Class</Form.Label><Form.Control value={ticket.classType} onChange={(e) => update("classType", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Quota</Form.Label><Form.Control value={ticket.quota} onChange={(e) => update("quota", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Boarding Point</Form.Label><Form.Control value={ticket.boardingPoint} onChange={(e) => update("boardingPoint", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Platform</Form.Label><Form.Control value={ticket.platformNumber} onChange={(e) => update("platformNumber", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Coach</Form.Label><Form.Control value={ticket.coach} onChange={(e) => update("coach", e.target.value)} /></Form.Group>
        </Row>
      </Section>

      <Section title="Passengers">
        {ticket.passengers.map((p, i) => (
          <div key={i} className={classes.PassengerCard}>
            <div className={classes.PassengerHeader}>
              <strong>Passenger {i + 1}</strong>
              <div>
                <Button size="sm" variant="outline-secondary" onClick={() => movePassenger(i, -1)} disabled={i === 0}>↑</Button>{" "}
                <Button size="sm" variant="outline-secondary" onClick={() => movePassenger(i, 1)} disabled={i === ticket.passengers.length - 1}>↓</Button>{" "}
                <Button size="sm" variant="outline-danger" onClick={() => removePassenger(i)} disabled={ticket.passengers.length <= 1}>Remove</Button>
              </div>
            </div>
            <Row>
              <Form.Group as={Col} md={4}><Form.Label>Name</Form.Label><Form.Control value={p.passengerName} onChange={(e) => updatePassenger(i, "passengerName", e.target.value)} /></Form.Group>
              <Form.Group as={Col} md={2}><Form.Label>Age</Form.Label><Form.Control type="number" min="0" value={p.age} onChange={(e) => updatePassenger(i, "age", e.target.value)} /></Form.Group>
              <Form.Group as={Col} md={2}><Form.Label>Gender</Form.Label><Form.Control as="select" value={p.gender} onChange={(e) => updatePassenger(i, "gender", e.target.value)}><option value="">—</option>{GENDER_OPTIONS.map((o) => <option key={o}>{o}</option>)}</Form.Control></Form.Group>
              <Form.Group as={Col} md={4}><Form.Label>Passenger Type</Form.Label><Form.Control as="select" value={p.passengerType} onChange={(e) => updatePassenger(i, "passengerType", e.target.value)}>{PASSENGER_TYPE_OPTIONS.map((o) => <option key={o}>{o}</option>)}</Form.Control></Form.Group>
              <Form.Group as={Col} md={3}><Form.Label>Coach</Form.Label><Form.Control value={p.coach} onChange={(e) => updatePassenger(i, "coach", e.target.value)} /></Form.Group>
              <Form.Group as={Col} md={3}><Form.Label>Seat/Berth No.</Form.Label><Form.Control value={p.seatNumber} onChange={(e) => updatePassenger(i, "seatNumber", e.target.value)} /></Form.Group>
              <Form.Group as={Col} md={3}><Form.Label>Berth Type</Form.Label><Form.Control as="select" value={p.berthType} onChange={(e) => updatePassenger(i, "berthType", e.target.value)}><option value="">—</option>{BERTH_TYPE_OPTIONS.map((o) => <option key={o}>{o}</option>)}</Form.Control></Form.Group>
              <Form.Group as={Col} md={3}><Form.Label>Status</Form.Label><Form.Control as="select" value={p.ticketStatus} onChange={(e) => updatePassenger(i, "ticketStatus", e.target.value)}>{PASSENGER_STATUS_OPTIONS.map((o) => <option key={o}>{o}</option>)}</Form.Control></Form.Group>
              <Form.Group as={Col} md={3}><Form.Label>ID Type</Form.Label><Form.Control value={p.idType} onChange={(e) => updatePassenger(i, "idType", e.target.value)} /></Form.Group>
              <Form.Group as={Col} md={3}><Form.Label>ID Number</Form.Label><Form.Control value={p.idNumber} onChange={(e) => updatePassenger(i, "idNumber", e.target.value)} /></Form.Group>
              <Form.Group as={Col} md={3}><Form.Label>Meal Preference</Form.Label><Form.Control value={p.mealPreference} onChange={(e) => updatePassenger(i, "mealPreference", e.target.value)} /></Form.Group>
              <Form.Group as={Col} md={3}><Form.Label>Berth Preference</Form.Label><Form.Control value={p.berthPreference} onChange={(e) => updatePassenger(i, "berthPreference", e.target.value)} /></Form.Group>
              <Form.Group as={Col} md={12}><Form.Label>Remarks</Form.Label><Form.Control value={p.remarks} onChange={(e) => updatePassenger(i, "remarks", e.target.value)} /></Form.Group>
            </Row>
          </div>
        ))}
        <Button variant="outline-primary" onClick={addPassenger}>+ Add Passenger</Button>
      </Section>

      <Section title="Fare Details">
        <Row>
          {fareField("Base Fare", "baseFare")}
          {fareField("Reservation Charge", "reservationCharge")}
          {fareField("Superfast Charge", "superfastCharge")}
          {fareField("Catering Charge", "cateringCharge")}
          {fareField("GST", "gst")}
          {fareField("Convenience Fee", "convenienceFee")}
          {fareField("Service Fee", "serviceFee")}
          {fareField("Insurance", "insurance")}
          {fareField("Other Charges", "otherCharges")}
          {fareField("Discount", "discount")}
          {fareField("Coupon Discount", "couponDiscount")}
          {fareField("Wallet Amount", "walletAmount")}
        </Row>
        <Row className="align-items-center mt-2">
          <Col md={4}>
            <strong>Calculated Total: ₹{fareTotal.toFixed(2)}</strong>
          </Col>
          <Col md={4}>
            <Form.Check
              type="checkbox"
              label="Manual override final amount"
              checked={ticket.manualOverride}
              onChange={(e) => update("manualOverride", e.target.checked)}
            />
          </Col>
          {ticket.manualOverride && (
            <Form.Group as={Col} md={4}>
              <Form.Label>Manual Final Amount</Form.Label>
              <Form.Control type="number" min="0" step="0.01" value={ticket.manualFinalAmount} onChange={(e) => update("manualFinalAmount", e.target.value)} />
            </Form.Group>
          )}
        </Row>
      </Section>

      <Section title="Customer Details">
        <Row>
          <Form.Group as={Col} md={4}><Form.Label>Name</Form.Label><Form.Control value={ticket.customerName} onChange={(e) => update("customerName", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Mobile</Form.Label><Form.Control value={ticket.customerMobile} onChange={(e) => update("customerMobile", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Email</Form.Label><Form.Control type="email" value={ticket.customerEmail} onChange={(e) => update("customerEmail", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={12}><Form.Label>Address</Form.Label><Form.Control as="textarea" rows={2} value={ticket.customerAddress} onChange={(e) => update("customerAddress", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>City</Form.Label><Form.Control value={ticket.customerCity} onChange={(e) => update("customerCity", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>State</Form.Label><Form.Control value={ticket.customerState} onChange={(e) => update("customerState", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={4}><Form.Label>Country</Form.Label><Form.Control value={ticket.customerCountry} onChange={(e) => update("customerCountry", e.target.value)} /></Form.Group>
        </Row>
      </Section>

      <Section title="QR / Barcode">
        <p className={classes.Hint}>
          Barcode is automatically generated from PNR (same as flight tickets).
        </p>
        <Row>
          <Form.Group as={Col} md={6}>
            <Form.Label>QR Code Data</Form.Label>
            <Form.Control value={ticket.qrCodeData} onChange={(e) => update("qrCodeData", e.target.value)} placeholder="Optional — generates QR if provided" />
          </Form.Group>
          <Form.Group as={Col} md={6}>
            <Form.Label>Barcode (from PNR)</Form.Label>
            <Form.Control readOnly value={ticket.pnr || ""} placeholder="Enter PNR above to generate barcode" />
          </Form.Group>
        </Row>
      </Section>

      <Section title="Additional Information">
        <Row>
          <Form.Group as={Col} md={6}><Form.Label>Boarding Instructions</Form.Label><Form.Control as="textarea" rows={3} value={ticket.boardingInstructions} onChange={(e) => update("boardingInstructions", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={6}><Form.Label>Cancellation Policy</Form.Label><Form.Control as="textarea" rows={3} value={ticket.cancellationPolicy} onChange={(e) => update("cancellationPolicy", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={6}><Form.Label>Important Information</Form.Label><Form.Control as="textarea" rows={3} value={ticket.importantInformation} onChange={(e) => update("importantInformation", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={6}><Form.Label>Terms & Conditions</Form.Label><Form.Control as="textarea" rows={3} value={ticket.termsConditions} onChange={(e) => update("termsConditions", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={6}><Form.Label>Customer Remarks</Form.Label><Form.Control as="textarea" rows={2} value={ticket.customerRemarks} onChange={(e) => update("customerRemarks", e.target.value)} /></Form.Group>
          <Form.Group as={Col} md={6}><Form.Label>Internal Remarks (not on ticket)</Form.Label><Form.Control as="textarea" rows={2} value={ticket.internalRemarks} onChange={(e) => update("internalRemarks", e.target.value)} /></Form.Group>
        </Row>
      </Section>

      <div className={classes.Actions}>
        <Button variant="primary" onClick={handlePreview}>Preview Ticket</Button>
        <Button variant="success" className="ml-2" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
        <Button variant="info" className="ml-2" onClick={handleDownload}>Download PDF</Button>
      </div>

      <TrainTicketPreview
        show={showPreview}
        html={previewHtml}
        onClose={() => setShowPreview(false)}
        onEdit={() => setShowPreview(false)}
        onSave={handleSave}
        onPrint={() => {}}
        onDownload={handleDownload}
        saving={saving}
      />
    </div>
  );
};

export default TrainTicketForm;
